import { QueryKeys } from "@/constants/Query";
import axiosInstance from "@/lib/axios";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { getConfigCatFeatureFlagOverrides } from "./configCatFeatureFlagOverrides";

// This API returns a primitive value, not JSON. Config cat supports boolean, string,
// whole number, and float feature flag types.
export type ConfigCatFeatureFlagResponse =
  | boolean
  | string
  | number
  | null
  | undefined;

/**
 * Fetches the variation for a specific feature flag by its name.
 * @param flagName The name of the feature flag to fetch.
 * @returns The result of the query, containing the flag's value (boolean or string).
 */
export const useConfigCatFeatureFlag = (
  flagName: string,
): UseQueryResult<ConfigCatFeatureFlagResponse, Error> => {
  const featureFlagOverrides = getConfigCatFeatureFlagOverrides();
  const hasOverride = Object.prototype.hasOwnProperty.call(
    featureFlagOverrides,
    flagName,
  );
  const overrideValue = hasOverride
    ? featureFlagOverrides[flagName]
    : undefined;
  const gatewayQueryKey = QueryKeys.FEATURE_FLAGS.CONFIG_CAT_FLAG(flagName);
  const queryKey = hasOverride
    ? [...gatewayQueryKey, "local-override"]
    : gatewayQueryKey;

  return useQuery({
    queryKey: queryKey,
    enabled: !hasOverride,
    initialData: hasOverride ? overrideValue : undefined,
    queryFn: async () => {
      if (hasOverride) {
        return overrideValue;
      }

      const response = await axiosInstance.get<ConfigCatFeatureFlagResponse>(
        `/feature-flags/variation/configcat/${flagName}`,
      );
      return response.data;
    },
  });
};
